// Threshold value resolution and display partitioning for installation setup.

export function thresholdKey(row) {
  return [row.parameter_id, row.threshold_type_id, row.alert_type_id, row.comparison_operator_id]
    .map((value) => (value == null ? "" : String(value)))
    .join(":");
}

// 0 and "0" are populated values; only null/undefined/blank strings are empty.
export function isPopulated(value) {
  if (value == null) return false;
  return String(value).trim() !== "";
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined);
}

// Resolution order: installer edit (including an explicit clear), saved draft,
// existing device threshold, model default. Missing values fall through.
export function resolveThresholdValue({ edit, draft, existing, definition }) {
  if (edit && Object.prototype.hasOwnProperty.call(edit, "threshold_value")) return edit.threshold_value ?? "";
  if (draft && isPopulated(draft.threshold_value)) return String(draft.threshold_value);
  if (existing && isPopulated(existing.threshold_value)) return String(existing.threshold_value);
  if (definition && isPopulated(definition.default_value)) return String(definition.default_value);
  return "";
}

function flag(edit, draft, existing, definition, field) {
  const value = firstPresent(edit?.[field], draft?.[field], existing?.[field], definition?.[field]);
  return Boolean(value);
}

function baseLabel(definition, lookups) {
  return (
    definition?.DeviceParameter?.label ||
    definition?.DeviceParameter?.parameter_name ||
    lookups?.parameterMap?.get(Number(definition.parameter_id)) ||
    `Parameter ${definition.parameter_id}`
  );
}

// Distinguish repeated parameters (e.g. several Percent Level definitions) by
// their comparison/type metadata, then by alert type, never by position alone.
export function labelThresholds(definitions, lookups) {
  const details = definitions.map((definition) => {
    const base = baseLabel(definition, lookups);
    const operator =
      definition?.ComparisonOperator?.label ||
      lookups?.operatorMap?.get(Number(definition.comparison_operator_id)) ||
      "";
    const type =
      definition?.DeviceMonitorThresholdType?.threshold_name ||
      lookups?.typeMap?.get(Number(definition.threshold_type_id)) ||
      "";
    const alertType = definition?.AlertType?.type_name || lookups?.alertTypeMap?.get(Number(definition.alert_type_id)) || "";
    return { base, operator, type, alertType };
  });
  const counts = new Map();
  details.forEach((d) => counts.set(d.base, (counts.get(d.base) || 0) + 1));
  const withMeta = details.map((d) =>
    counts.get(d.base) > 1 ? [d.base, [d.type, d.operator].filter(Boolean).join(" ")].filter(Boolean).join(" - ") : d.base,
  );
  const metaCounts = new Map();
  withMeta.forEach((label) => metaCounts.set(label, (metaCounts.get(label) || 0) + 1));
  return withMeta.map((label, index) => {
    if (metaCounts.get(label) > 1 && details[index].alertType) return `${label} (${details[index].alertType})`;
    if (metaCounts.get(label) > 1) return `${label} (definition ${index + 1})`;
    return label;
  });
}

export function buildThresholdRows({ definitions = [], existing = [], draft = [], edits = {}, lookups }) {
  const existingByKey = new Map(
    existing.filter((row) => row.channel_number == null && row.group_id == null).map((row) => [thresholdKey(row), row]),
  );
  const draftByKey = new Map((draft || []).map((row) => [thresholdKey(row), row]));
  const labels = labelThresholds(definitions, lookups);
  return definitions.map((definition, index) => {
    const key = thresholdKey(definition);
    const edit = edits[key];
    const current = existingByKey.get(key);
    const saved = draftByKey.get(key);
    return {
      key,
      parameter_id: definition.parameter_id,
      threshold_type_id: definition.threshold_type_id ?? null,
      alert_type_id: definition.alert_type_id ?? null,
      comparison_operator_id: definition.comparison_operator_id ?? null,
      parameter_label: labels[index],
      unit: definition?.DeviceParameter?.format || definition.unit || "",
      required: Boolean(definition.is_required),
      visible: definition.show_on_create !== false && definition.show_on_create !== 0,
      threshold_value: resolveThresholdValue({ edit, draft: saved, existing: current, definition }),
      alert_enabled: flag(edit, saved, current, definition, "alert_enabled"),
      event_enabled: flag(edit, saved, current, definition, "event_enabled"),
      had_saved_value: isPopulated(current?.threshold_value),
    };
  });
}

// Display-only partition: hiding never changes values or flags.
export function partitionThresholds(rows, { expanded = false } = {}) {
  const primary = [];
  const optional = [];
  const conflicts = [];
  rows.forEach((row, index) => {
    const entry = { row, index };
    if (row.required && !row.visible) conflicts.push(entry);
    if (isPopulated(row.threshold_value) || row.required) primary.push(entry);
    else optional.push(entry);
  });
  return { primary, optional, conflicts, showOptional: expanded };
}

export function missingRequiredThresholds(rows) {
  return rows.filter((row) => row.required && !isPopulated(row.threshold_value));
}

// Unset optional entries are omitted from completion, matching the existing payload.
export function submittedThresholdRows(rows) {
  return rows
    .filter((row) => isPopulated(row.threshold_value))
    .map((row) => ({
      parameter_id: Number(row.parameter_id),
      threshold_type_id: row.threshold_type_id == null ? null : Number(row.threshold_type_id),
      alert_type_id: row.alert_type_id == null ? null : Number(row.alert_type_id),
      comparison_operator_id: row.comparison_operator_id == null ? null : Number(row.comparison_operator_id),
      threshold_value: String(row.threshold_value).trim(),
      alert_enabled: Boolean(row.alert_enabled),
      event_enabled: Boolean(row.event_enabled),
    }));
}
